import { useTableScrollHints } from "../useTableScrollHints";
import { useRef } from "react";
import { callLine, type CardState, type ChatMessage } from "../protocol";
import { renderMarkdown } from "../md";
import { linkMentions, type Mention } from "../mentions";
import { timeLabel } from "../format";
import { LazyImage, type LoadImage } from "./LazyImage";
import { LazyFile } from "./LazyFile";
import { rendersAsFile } from "../attachments";
import { docCardTitle } from "../docCard";

/** A chat message: images (single/gallery), markdown text, decision cards + quick-reply chips, and quotes. */
export function MessageBubble({
  m,
  load,
  onOpen,
  onOption,
  onOpenDoc,
  cardState,
  onCardDismiss,
  quoted,
  addressed,
  sessionNames,
  onOpenSession,
}: {
  m: ChatMessage;
  load: LoadImage;
  onOpen?: (url: string) => void;
  onOption?: (text: string, extra?: { cardId?: string; optionId?: string }) => void;
  onOpenDoc?: (title: string, docId: string) => void;
  /** How this message's card ended; undefined while it is still open and answerable. */
  cardState?: CardState;
  onCardDismiss?: (cardId: string) => void;
  quoted?: string;
  /** Group threads: this message is an agent talking to the observer rather than to another agent. */
  addressed?: boolean;
  /** The sessions an `@name` in the text links to. */
  sessionNames?: Mention[];
  /** Opens a session's chat, by its session id, when its `@name` is clicked. */
  onOpenSession?: (session: string) => void;
}) {
  const gallery = m.atts && m.atts.length > 0 ? m.atts : undefined;
  // Covers both the message text and a decision card's body — both render markdown inside this bubble.
  const bubbleRef = useRef<HTMLDivElement>(null);
  useTableScrollHints(bubbleRef, [m.text, m.card?.body]);
  return (
    <div className={`bubble ${m.role}${addressed ? " addressed" : ""}`} data-msg-id={m.id} ref={bubbleRef}>
      {quoted !== undefined && <div className="quote">{quoted || "(message)"}</div>}
      {/* A recap says it was a call, so the reader doesn't have to infer it from the heading the agent wrote.
          Same words the phone puts on its call list, off the same marker. */}
      {m.kind === "call_recap" && m.call && <div className="call-line">📞 {callLine(m.call)}</div>}
      {m.att && !gallery && rendersAsFile(m.mime) && (
        <LazyFile att={m.att} mime={m.mime} name={m.attName} load={load} />
      )}
      {m.att && !gallery && !rendersAsFile(m.mime) && (
        <LazyImage att={m.att} mime={m.mime ?? "image/*"} load={load} className="msg-img" onOpen={onOpen} />
      )}
      {gallery && (
        <div className={`gallery n${Math.min(gallery.length, 4)}`}>
          {gallery.map((a, i) => (
            <LazyImage key={a} att={a} mime={m.mimes?.[i] ?? "image/*"} load={load} className="gallery-img" onOpen={onOpen} />
          ))}
        </div>
      )}
      {m.text && (
        <div
          className="bubble-text md"
          onClick={(e) => {
            const a = (e.target as HTMLElement).closest<HTMLElement>("a.mention");
            if (!a?.dataset.session) return;
            e.preventDefault();
            onOpenSession?.(a.dataset.session);
          }}
          dangerouslySetInnerHTML={{ __html: linkMentions(renderMarkdown(m.text), onOpenSession ? (sessionNames ?? []) : []) }}
        />
      )}
      {/* Labelled by the document, never by the message: the message says its own thing, in its own bubble. */}
      {m.docId && (
        <button className="doc-card" onClick={() => onOpenDoc?.(docCardTitle(m.docName), m.docId!)}>
          <span className="doc-icon">📄</span>
          <span className="doc-meta">
            <span className="doc-title">{docCardTitle(m.docName)}</span>
            <span className="doc-sub">Tap to read</span>
          </span>
        </button>
      )}
      {m.card && (
        <div className="card">
          {m.card.body && (
            <div className="bubble-text md" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.card.body) }} />
          )}
          {cardState === "ran" ? (
            <div className="card-resolved ran">✓ Ran</div>
          ) : cardState === "failed" ? (
            <div className="card-resolved failed">✗ Failed</div>
          ) : cardState === "answered" ? (
            <div className="card-resolved">Answered ✓</div>
          ) : cardState === "cleared" ? (
            // Deliberately not "Answered": the question was never decided, only taken off the screen.
            <div className="card-resolved cleared">Dismissed</div>
          ) : cardState === "expired" ? (
            <div className="card-resolved cleared">Expired unanswered</div>
          ) : (
            <div className="card-options">
              {m.card.options.map((o) => (
                <button
                  key={o.id}
                  className="card-btn"
                  onClick={() => onOption?.(o.label, { cardId: m.card!.id, optionId: o.id })}
                >
                  {o.label}
                </button>
              ))}
              <button
                className="card-dismiss"
                title="Clear this card without answering it"
                onClick={() => onCardDismiss?.(m.card!.id)}
              >
                ✕ Dismiss
              </button>
            </div>
          )}
        </div>
      )}
      {m.options && m.options.length > 0 && (
        <div className="quick-replies">
          {m.options.map((o, i) => (
            <button key={i} className="chip" onClick={() => onOption?.(o)}>
              {o}
            </button>
          ))}
        </div>
      )}
      <span className="meta">
        <span className="time">{timeLabel(m.ts)}</span>
        {m.role === "user" && (
          <span className={`receipt ${m.receipt ?? "sent"}`} title={m.receipt ?? "sent"}>
            {m.receipt === "ack" ? "👍" : m.receipt === "read" || m.receipt === "delivered" ? "✓✓" : "✓"}
          </span>
        )}
      </span>
    </div>
  );
}
